import { CSSProperties, FunctionComponent, ReactFragment, ReactNode, createElement } from 'react';
import { cssVars as palette } from '../../theme/cssVars';

type Variant =
  | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  | 'subtitle1' | 'subtitle2' | 'body1' | 'body2'
  | 'caption' | 'button' | 'overline' | 'inherit';

type TypographyUIProps = {
  text: string | number | undefined,
  variant?: Variant,
  align?: 'inherit' | 'left' | 'center' | 'right' | 'justify',
  color?: 'initial' | 'inherit' | 'primary' | 'secondary' | 'textPrimary' | 'textSecondary' | 'error',
  paragraph?: true | false,
  display?: 'inline' | 'initial' | 'block',
  fontWeight?: 'inherit' | 'initial' | 'revert' | 'unset' | 'bold' | 'bolder' | 'normal' | 'lighter' | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900,
  margin?: [number, number, number, number],
  padding?: [number, number, number, number],
  cursor?: 'pointer',
  textColor?: string | undefined,
  icon?: ReactFragment,
  textDecoration?: boolean,
  customFontSize?: number,
  alignSelf?: 'auto' | 'normal' | 'center' | 'start' | 'end',
  children?: ReactNode
};

// MUI type-ramp metrics. h1–h6 mirror src/theme/typography.tsx (merged onto the
// MUI defaults it doesn't override, e.g. h1 lineHeight); the rest are MUI's
// default theme values at a 16px root. Kept as inline styles rather than the
// tailwind.config `text-h*` tokens on purpose: `important: '#root'` would make a
// utility class win over this component's dynamic inline overrides
// (customFontSize / textColor / margin), so inline is the coexistence-safe port.
// Type ramp, driven by the design-system size tokens so it tracks the density
// setting (`--ds-fs-*` shrink one step in compact mode).
//
// Two deliberate corrections to the old MUI-derived ramp:
//  - h3 was fontWeight 200 while h4 was 400, so an h3 heading rendered LIGHTER
//    than the h4 beneath it. Headings now descend 600 → 500 monotonically.
//  - the old ramp started at 35px and body1 was 16px, which is most of why the
//    app reads as oversized. The scale now tops out at --ds-fs-2xl (24px) and
//    body text sits at --ds-fs-md (13px compact / 14px comfortable).
//
// Still inline styles rather than the `text-h*` utilities: `important: '#root'`
// would make a utility class beat this component's dynamic overrides
// (customFontSize / textColor / fontWeight), so inline is the coexistence-safe
// port. See the note in MIGRATION.md.
const variantStyle: Record<Variant, CSSProperties> = {
  h1: { fontSize: 'var(--ds-fs-2xl)', fontWeight: 600, lineHeight: 1.25, letterSpacing: '-0.02em' },
  h2: { fontSize: 'var(--ds-fs-xl)', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.015em' },
  h3: { fontSize: 'var(--ds-fs-lg)', fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.01em' },
  h4: { fontSize: 'var(--ds-fs-md)', fontWeight: 600, lineHeight: 1.4 },
  h5: { fontSize: 'var(--ds-fs-sm)', fontWeight: 500, lineHeight: 1.4 },
  h6: { fontSize: 'var(--ds-fs-xs)', fontWeight: 500, lineHeight: 1.4 },
  subtitle1: { fontSize: 'var(--ds-fs-md)', fontWeight: 500, lineHeight: 1.5 },
  subtitle2: { fontSize: 'var(--ds-fs-sm)', fontWeight: 500, lineHeight: 1.5 },
  body1: { fontSize: 'var(--ds-fs-md)', fontWeight: 400, lineHeight: 1.5 },
  body2: { fontSize: 'var(--ds-fs-sm)', fontWeight: 400, lineHeight: 1.45 },
  caption: { fontSize: 'var(--ds-fs-xs)', fontWeight: 400, lineHeight: 1.45 },
  button: { fontSize: 'var(--ds-fs-sm)', fontWeight: 500, lineHeight: 1.5, letterSpacing: '0.01em' },
  overline: { fontSize: 'var(--ds-fs-xs)', fontWeight: 600, lineHeight: 1.6, textTransform: 'uppercase', letterSpacing: '0.07em' },
  inherit: { fontSize: 'inherit', fontWeight: 'inherit', lineHeight: 'inherit', letterSpacing: 'inherit' },
};

// MUI `color` prop → palette hex (src/theme/index.tsx). undefined / inherit → not set (inherits).
const colorValue: Record<Exclude<TypographyUIProps['color'], undefined | 'inherit'>, string> = {
  initial: 'initial',
  primary: palette.primary,
  secondary: palette.secondary,
  textPrimary: palette.content,
  textSecondary: palette.muted,
  error: palette.error,
};

// MUI variantMapping → semantic element. `paragraph` forces <p>.
const variantElement: Record<Variant, string> = {
  h1: 'h1', h2: 'h2', h3: 'h3', h4: 'h4', h5: 'h5', h6: 'h6',
  subtitle1: 'h6', subtitle2: 'h6', body1: 'p', body2: 'p',
  caption: 'span', button: 'span', overline: 'span', inherit: 'p',
};

const TypographyUI: FunctionComponent<TypographyUIProps> = ({
  text, variant, align, color, paragraph, fontWeight, margin, cursor,
  textColor, icon, display = 'block', textDecoration, customFontSize, padding, alignSelf,
}) => {
  const px = 8;
  // variant defaults to body1 (matches MUI's default when variant is omitted)
  const resolvedVariant: Variant = variant ?? 'body1';

  // margin/padding arrays are in MUI spacing units (× 8px). Always set all four
  // (default 0) so raw h1–p tags don't inherit UA margins with preflight disabled.
  const m = margin ?? [0, 0, 0, 0];
  const p = padding ?? [0, 0, 0, 0];

  // textColor (raw string) overrides the semantic color prop, matching the old wrapper.
  const resolvedColor =
    textColor !== undefined ? textColor
      : color && color !== 'inherit' ? colorValue[color]
        : undefined;

  const style: CSSProperties = {
    ...variantStyle[resolvedVariant],
    // fontWeight prop overrides the variant weight when provided
    ...(fontWeight !== undefined ? { fontWeight } : {}),
    ...(customFontSize !== undefined ? { fontSize: customFontSize } : {}),
    marginTop: `${m[0] * px}px`,
    marginRight: `${m[1] * px}px`,
    marginBottom: `${m[2] * px}px`,
    marginLeft: `${m[3] * px}px`,
    paddingTop: `${p[0] * px}px`,
    paddingRight: `${p[1] * px}px`,
    paddingBottom: `${p[2] * px}px`,
    paddingLeft: `${p[3] * px}px`,
    display,
    textAlign: align,
    alignSelf,
    cursor: cursor ?? 'default',
    textDecoration: textDecoration ? 'underline' : undefined,
    ...(resolvedColor !== undefined ? { color: resolvedColor } : {}),
  };

  const element = paragraph ? 'p' : variantElement[resolvedVariant];

  return createElement(
    element,
    { style },
    icon !== undefined && icon,
    ' ',
    text,
  );
};

export default TypographyUI;
